import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import readXlsxFile from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';
import { AntecedentValueCheckService } from '../services/antecedent-value-check.service';

@Component({
  selector: 'app-antecedent-value-check-upload',
  imports: [CommonModule],
  templateUrl: './antecedent-value-check-upload.html',
  styleUrl: './antecedent-value-check-upload.css',
})
export class AntecedentValueCheckUpload {
  excelFilePath = signal<string>('');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isSending = signal<boolean>(false);
  isValidExcel = signal<boolean>(false);

  readonly REQUIRED_HEADERS = [
    'Cartera Id',
    'Quien ha generado el Antecedente',
    'Fecha antecedente',
    'Referencia Catastral',
    'IDUFIR',
    'Valor AVM',
    'Diferencial (+/- porcentaje) (si es vacio, se usará las reglas de validación automatica)',
    'Observaciones'
  ];

  private selectedExcelFile: File | null = null;

  constructor(private router: Router, private antecedentValueCheckService: AntecedentValueCheckService) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectExcelFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.xlsx,.xls');

    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.selectedExcelFile = file;
        this.excelFilePath.set(file.name);
        this.errorMessage.set('');
        this.successMessage.set('');
        this.isValidExcel.set(false);
        await this.validateExcelHeaders(file);
      }
    };

    input.click();
  }

  async validateExcelHeaders(file: File): Promise<void> {
    try {
      const rows = await readXlsxFile(file);
      if (rows.length === 0) {
        this.errorMessage.set('El Excel no tiene el formato correcto');
        return;
      }
      const headers = rows[0].map(cell => (cell !== null && cell !== undefined ? String(cell) : ''));
      const headersMatch =
        headers.length === this.REQUIRED_HEADERS.length &&
        headers.every((h, i) => h === this.REQUIRED_HEADERS[i]);
      if (!headersMatch) {
        this.errorMessage.set('El Excel no tiene el formato correcto. Las columnas deben ser: ' + this.REQUIRED_HEADERS.join(', '));
      } else {
        this.isValidExcel.set(true);
      }
    } catch {
      this.errorMessage.set('El Excel no tiene el formato correcto');
    }
  }

  canSend(): boolean {
    return !!this.selectedExcelFile && this.isValidExcel() && !this.isSending();
  }

  async downloadExampleExcel(): Promise<void> {
    const EXAMPLE_ROW_1 = [
      '648', 'AVM', '20/02/2026', '9502I02YHI590B0001KZ', '30540008OI263',
      '75.292,47', '10,00', ''
    ];
    const EXAMPLE_ROW_2 = [
      '648', 'AVM', '20/02/2026', '940260IYH590C0005KA', '305400082O370',
      '53.767,31', '', ''
    ];
    const data = [this.REQUIRED_HEADERS, EXAMPLE_ROW_1, EXAMPLE_ROW_2];
    await writeXlsxFile(data, { fileName: 'ejemplo_antecedentes_backtesting.xlsx' });
  }

  async sendRequest() {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    try {
      await firstValueFrom(
        this.antecedentValueCheckService.insertAntecedentForValueCheck(this.selectedExcelFile!)
      );
      this.successMessage.set('Carga de valores antecedentes backtesting enviada correctamente');
    } catch (error: any) {
      const message = error.error?.message || error.message || 'Error desconocido al enviar la solicitud';
      this.errorMessage.set(`Error en el envío: ${message}`);
    } finally {
      this.isSending.set(false);
    }
  }
}
